// frontend/src/pages/ManageOrders.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import RatingForm from '../components/RatingForm';
import { messaging, onMessage } from '../firebase';
import './Home.css';
import './ManageOrders.css';

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const API_URL = process.env.REACT_APP_API_URL;
const WS_PROTOCOL = API_URL.startsWith('https') ? 'wss://' : 'ws://';
const WS_HOST = API_URL.replace(/^https?:\/\//, '');
const WS_URL = `${WS_PROTOCOL}${WS_HOST}`;

// EXACT SAME SPINNER AS ProviderProfile.js — BEAUTIFUL WHITE
const Spinner = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Global spinner animation (once)
if (!document.getElementById('manage-orders-spinner')) {
  const style = document.createElement('style');
  style.id = 'manage-orders-spinner';
  style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

const fetchOrders = async () => {
  const response = await axios.get(`${API_URL}/api/provider_orders/`, { withCredentials: true });
  return response.data;
};

const fetchClientOrders = async () => {
  const response = await axios.get(`${API_URL}/api/client_orders/`, { withCredentials: true });
  return response.data;
};

const fetchOrderProgress = async (requestId) => {
  const response = await axios.get(`${API_URL}/api/order_progress/${requestId}/`, { withCredentials: true });
  return response.data;
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'ready_for_payment', label: 'Ready for Payment' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'voided', label: 'Voided' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_payment', label: 'Pending Payment' },
];

const OrderSection = React.memo(({
  title,
  orders,
  filters,
  setFilters,
  currentPage,
  setCurrentPage,
  isCompleted,
  isProviderSection,
  sectionKey,
  providerOrders,
  handleAccept,
  handleReject,
  getStatusColor,
  showJobDonePanel,
  setShowJobDonePanel,
  showPaymentRequestPanel,
  setShowPaymentRequestPanel,
  jobDoneAmount,
  setJobDoneAmount,
  payoutPhone,
  setPayoutPhone,
  clientPayPhone,
  setClientPayPhone,
  queryClient,
  user,
  orderRefs,
  getMinCharge,
  acceptMutation,
  rejectMutation,
  markJobCompleteMutation,
  initiateStkPushMutation,
  paymentStates,
  setPaymentStates,
}) => {
  const sectionRef = useRef(null);
  const ordersPerPage = 10;

  const filterOrders = useCallback((orders, filters) => {
    if (!orders || !Array.isArray(orders)) return [];
    return orders.filter((req) => {
      const status = req.order?.status || req.status;
      const matchesStatus = !filters.statuses.length || filters.statuses.some(s => s.value === status);
      const matchesSearch = !filters.search || (req.description?.toLowerCase().includes(filters.search.toLowerCase()));
      return matchesStatus && matchesSearch;
    });
  }, []);

  const paginateOrders = useCallback((orders, page) => {
    if (!orders || !Array.isArray(orders)) return [];
    const start = (page - 1) * ordersPerPage;
    return orders.slice(start, start + ordersPerPage);
  }, []);

  const filteredOrders = useMemo(() => filterOrders(orders, filters), [orders, filters, filterOrders]);
  const paginatedOrders = useMemo(() => paginateOrders(filteredOrders, currentPage), [filteredOrders, currentPage, paginateOrders]);

  const aiohttp = useMemo(() => debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  }, 300), [setFilters, setCurrentPage]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      searchInput: { ...prev.searchInput, [sectionKey]: value }
    }));
    aiohttp(value);
  }, [sectionKey, aiohttp, setFilters]);

  const handleStatusChange = useCallback((selected) => {
    setFilters(prev => ({ ...prev, statuses: selected || [] }));
    setCurrentPage(1);
  }, [setFilters, setCurrentPage]);

  const confirmProgressMutation = useMutation({
    mutationFn: async ({ requestId }) => {
      const res = await axios.post(`${API_URL}/api/confirm_progress/${requestId}/`, {}, { withCredentials: true });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Progress confirmed');
      queryClient.invalidateQueries(['orderProgress']);
      queryClient.invalidateQueries(['providerOrders']);
      queryClient.refetchQueries(['providerOrders']);
    },
    onError: () => toast.error('Failed to confirm progress'),
  });

  const handleConfirmProgress = useCallback((requestId) => {
    confirmProgressMutation.mutate({ requestId });
  }, [confirmProgressMutation]);

  const Pagination = useCallback(({ currentPage, totalItems, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / ordersPerPage);
    const pages = [];
    const maxToShow = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    if (end - start + 1 < maxToShow) start = Math.max(1, end - maxToShow + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className="flex justify-center gap-2 mt-6 flex-wrap">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-50">Prev</button>
        {start > 1 && <button onClick={() => onPageChange(1)} className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600">1</button>}
        {start > 2 && <span className="self-center px-2 text-gray-400">...</span>}
        {pages.map(p => (
          <button key={p} onClick={() => onPageChange(p)}
            className={`px-4 py-2 rounded-lg ${p === currentPage ? 'bg-purple-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}>
            {p}
          </button>
        ))}
        {end < totalPages - 1 && <span className="self-center px-2 text-gray-400">...</span>}
        {end < totalPages && <button onClick={() => onPageChange(totalPages)}
          className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600">{totalPages}</button>}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-50">Next</button>
      </div>
    );
  }, []);

  return (
    <div className="mb-12" ref={sectionRef}>
      <h2 className="text-3xl font-bold text-blue-300 mb-6">{title}</h2>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select isMulti options={statusOptions} value={filters.statuses} onChange={handleStatusChange}
          placeholder="Filter by status..." className="flex-1" classNamePrefix="react-select"
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} menuPortalTarget={document.body} />
        <input type="text" value={filters.searchInput?.[sectionKey] || ''} onChange={handleSearchChange}
          placeholder="Search description..." className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white" />
        <button onClick={() => {
          setFilters({ statuses: [], search: '', searchInput: {} });
          setCurrentPage(1);
        }} className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-xl font-medium transition">
          Reset
        </button>
      </div>
      {paginatedOrders.length === 0 ? (
        <p className="text-gray-400 text-center py-12">No orders found</p>
      ) : (
        <>
          {paginatedOrders.map((req) => {
            const b2cTx = req.order?.mpesa_transactions?.find(tx => tx.transaction_type === 'B2C');
            const orderId = req.order?.id;
            const paymentState = orderId ? paymentStates[orderId] : null;

            return (
              <div key={req.id} ref={el => orderRefs.current[req.id] = el}
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-6 mb-6 shadow-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg">
                  <p><strong>{isProviderSection ? 'Client' : 'Provider'}:</strong> {isProviderSection ? req.user_username : req.provider_business_name}</p>
                  <p><strong>Description:</strong> {req.description || '—'}</p>
                  <p><strong>Scheduled:</strong> {req.scheduled_at ? new Date(req.scheduled_at).toLocaleString() : '—'}</p>
                  <p><strong>Status:</strong> <span className={getStatusColor(req.order?.status || req.status)}>{(req.order?.status || req.status).replace('_', ' ')}</span></p>
                  {req.estimated_amount && (
                    <p className="text-purple-400">
                      <strong>Client expected:</strong> KSh {req.estimated_amount}
                    </p>
                  )}
                  {req.order && (
                    <>
                      <p><strong>Amount:</strong> KSh {req.order.amount}</p>
                      {isProviderSection && b2cTx && (
                        <>
                          <p><strong>Net Payout:</strong> KSh {b2cTx.amount || '—'}</p>
                          <p><strong>B2C Fee Share:</strong> KSh {b2cTx.b2c_fee || '0'}</p>
                        </>
                      )}
                    </>
                  )}
                  {req.order?.rating && (
                    <p><strong>Rating:</strong> {req.order.rating.rating}/5 by {req.order.rating.display_name}</p>
                  )}
                </div>

                {req.status === 'active' && <ProgressTimer requestId={req.id} />}

                {isProviderSection && req.status === 'active' && (
                  <div className="mt-4">
                    <button
                      onClick={() => handleConfirmProgress(req.id)}
                      disabled={confirmProgressMutation.isPending}
                      className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-xl font-medium transition disabled:opacity-50 relative overflow-hidden flex items-center justify-center"
                    >
                      {confirmProgressMutation.isPending ? (
                        <>
                          <Spinner />
                          <span className="opacity-0">Confirm Progress</span>
                        </>
                      ) : (
                        'Confirm Progress'
                      )}
                    </button>
                  </div>
                )}

                {(req.status === 'scheduled' || req.status === 'active' || req.status === 'ready_for_payment') && (
                  <div className="mt-4">
                    <Link
                      to={`/profile/?chat_with=${isProviderSection ? req.user_id : req.provider_user_id}`}
                      className="block text-center w-full bg-indigo-600 hover:bg-indigo-700 py-3 rounded-xl font-medium transition"
                    >
                      Chat with {isProviderSection ? 'Client' : 'Provider'}
                    </Link>
                  </div>
                )}

                {isProviderSection && req.status === 'pending' && (
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => handleAccept(req.id)}
                      disabled={acceptMutation.isPending}
                      className="flex-1 bg-green-600 hover:bg-green-700 py-3 rounded-xl font-medium transition disabled:opacity-50 relative overflow-hidden flex items-center justify-center"
                    >
                      {acceptMutation.isPending ? (
                        <>
                          <Spinner />
                          <span className="opacity-0">Accept</span>
                        </>
                      ) : (
                        'Accept'
                      )}
                    </button>
                    <button
                      onClick={() => handleReject(req.id)}
                      disabled={rejectMutation.isPending}
                      className="flex-1 bg-red-600 hover:bg-red-700 py-3 rounded-xl font-medium transition disabled:opacity-50 relative overflow-hidden flex items-center justify-center"
                    >
                      {rejectMutation.isPending ? (
                        <>
                          <Spinner />
                          <span className="opacity-0">Reject</span>
                        </>
                      ) : (
                        'Reject'
                      )}
                    </button>
                  </div>
                )}

                {/* Provider: Job Done Button */}
                {isProviderSection && req.status === 'active' && !req.order && (
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        setShowJobDonePanel(req.id);
                        setJobDoneAmount(req.estimated_amount || '');
                        setPayoutPhone('');
                      }}
                      className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-6 rounded-2xl font-bold text-2xl shadow-2xl hover:from-emerald-600 hover:to-green-700 transition-all transform hover:scale-105"
                    >
                      Job Done – Charge Client
                    </button>
                  </div>
                )}

                {/* Client: Pay Now / Processing / Success / Retry */}
                {!isProviderSection && req.order && ['pending_payment', 'processing'].includes(req.order.status) && (
                  <div className="mt-8">
                    {paymentState === 'processing' ? (
                      <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-7 rounded-2xl font-bold text-3xl shadow-2xl flex items-center justify-center gap-3">
                        <Spinner />
                        Processing Payment...
                      </div>
                    ) : paymentState === 'success' ? (
                      <div className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-7 rounded-2xl font-bold text-3xl shadow-2xl text-center">
                        Payment Complete!
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setShowPaymentRequestPanel(req.id);
                          setClientPayPhone('');
                        }}
                        className={`w-full text-white py-7 rounded-2xl font-bold text-3xl shadow-2xl transition-all transform hover:scale-105 ${
                          req.order.status === 'processing' || paymentState === 'failed'
                            ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        }`}
                      >
                        {req.order.status === 'processing' || paymentState === 'failed'
                          ? 'Payment Failed – Try Again'
                          : `Pay KSh ${req.order.amount} Now`}
                      </button>
                    )}
                    {paymentState === 'processing' && (
                      <p className="text-center text-blue-300 text-sm mt-4 font-medium animate-pulse">
                        Waiting for M-Pesa confirmation... (up to 10 seconds)
                      </p>
                    )}
                  </div>
                )}

                {/* Provider Job Done Modal */}
                {showJobDonePanel === req.id && (
                  <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-md w-full border border-gray-700 p-8">
                      <h3 className="text-3xl font-bold text-white mb-6 text-center">Job Complete!</h3>
                      <div className="mb-6 p-5 bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl text-center border-2 border-purple-500 shadow-lg">
                        <p className="text-purple-100 text-sm uppercase tracking-wider mb-1 font-semibold">Minimum Charge</p>
                        <p className="text-4xl font-black text-white">KSh {getMinCharge(req)}</p>
                        <p className="text-purple-200 text-sm mt-2">You cannot charge below this amount</p>
                      </div>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-gray-300 font-medium mb-2 text-lg">
                            Final Amount Charged (KES)
                          </label>
                          <input
                            type="number"
                            value={jobDoneAmount}
                            onChange={(e) => {
                              const value = e.target.value;
                              const min = getMinCharge(req);
                              if (value && parseFloat(value) < min) {
                                toast.error(`Amount must be at least KSh ${min}`, { duration: 5000 });
                              }
                              setJobDoneAmount(value);
                            }}
                            placeholder={`Enter amount (min: KSh ${getMinCharge(req)})`}
                            min={getMinCharge(req)}
                            step="50"
                            className="w-full px-5 py-5 bg-gray-700 border-2 border-gray-600 rounded-xl text-white text-2xl text-center font-bold focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 transition-all placeholder-gray-500"
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="block text-gray-300 font-medium mb-2 text-lg">
                            Pay me on (M-Pesa number)
                          </label>
                          <input
                            type="text"
                            value={payoutPhone}
                            onChange={(e) => setPayoutPhone(e.target.value)}
                            placeholder="07xxxxxxxx"
                            className="w-full px-5 py-5 bg-gray-700 border border-gray-600 rounded-xl text-white text-lg focus:ring-4 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-4 mt-8">
                        <button
                          onClick={() => {
                            const amount = parseFloat(jobDoneAmount);
                            const min = getMinCharge(req);
                            if (!amount || amount < min) {
                              toast.error(`Minimum charge is KSh ${min}. Please enter a valid amount.`, {
                                duration: 6000,
                                style: { background: '#991b1b', color: 'white' },
                              });
                              return;
                            }
                            markJobCompleteMutation.mutate({
                              requestId: req.id,
                              amount: jobDoneAmount,
                              provider_phone: payoutPhone,
                            });
                          }}
                          disabled={
                            !jobDoneAmount ||
                            !payoutPhone ||
                            markJobCompleteMutation.isPending ||
                            (jobDoneAmount && parseFloat(jobDoneAmount) < getMinCharge(req))
                          }
                          className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white py-5 rounded-2xl font-bold text-xl shadow-xl hover:from-emerald-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 relative overflow-hidden flex items-center justify-center"
                        >
                          {markJobCompleteMutation.isPending ? (
                            <>
                              <Spinner />
                              <span className="opacity-0">Send Payment Request</span>
                            </>
                          ) : (
                            'Send Payment Request'
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowJobDonePanel(null);
                            setJobDoneAmount('');
                            setPayoutPhone('');
                          }}
                          className="flex-1 text-gray-400 hover:text-white py-5 border border-gray-600 rounded-2xl transition backdrop-blur-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Payment Modal */}
                {showPaymentRequestPanel === req.id && (
                  <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-md w-full border border-purple-600 p-8 text-center">
                      <h3 className="text-4xl font-bold text-white mb-6">Payment Request</h3>
                      <div className="bg-gradient-to-r from-purple-800 to-pink-800 rounded-2xl p-8 mb-8 border-4 border-purple-500">
                        <p className="text-5xl font-black text-white">KSh {req.order.amount}</p>
                        <p className="text-xl text-gray-200 mt-3">
                          Service by <span className="font-bold text-white">{req.provider_business_name}</span>
                        </p>
                      </div>
                      <div className="mb-8">
                        <label className="block text-left text-gray-300 font-medium mb-3 text-lg">
                          Pay from M-Pesa number
                        </label>
                        <input
                          type="text"
                          value={clientPayPhone}
                          onChange={e => setClientPayPhone(e.target.value)}
                          placeholder="07xxxxxxxx"
                          className="w-full px-6 py-5 bg-gray-700 border-2 border-gray-600 rounded-2xl text-white text-xl text-center focus:ring-4 focus:ring-purple-500"
                          autoFocus
                        />
                      </div>
                      <button
                        onClick={() => {
                          initiateStkPushMutation.mutate({
                            orderId: req.order.id,
                            phone_number: clientPayPhone
                          });
                          setPaymentStates(prev => ({ ...prev, [req.order.id]: 'processing' }));
                        }}
                        disabled={!clientPayPhone || initiateStkPushMutation.isPending}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 rounded-2xl font-bold text-2xl shadow-2xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all relative overflow-hidden flex items-center justify-center"
                      >
                        {initiateStkPushMutation.isPending ? (
                          <>
                            <Spinner />
                            <span className="opacity-0">Pay Now via M-Pesa</span>
                          </>
                        ) : (
                          'Pay Now via M-Pesa'
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowPaymentRequestPanel(null);
                          setClientPayPhone('');
                        }}
                        className="w-full mt-4 text-gray-400 hover:text-white py-4 border border-gray-600 rounded-2xl transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Rating Form */}
                {!isProviderSection && req.order?.status === 'completed' && !req.order.rating && (
                  <div className="mt-6">
                    <RatingForm
                      orderId= {req.order.id}
                      onSuccess={() => {
                        queryClient.invalidateQueries(['clientOrders']);
                        queryClient.invalidateQueries(['providerOrders']);
                        toast.success('Thank you for rating!');
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <Pagination currentPage={currentPage} totalItems={filteredOrders.length} onPageChange={setCurrentPage} />
        </>
      )}
    </div>
  );
});

const ProgressTimer = ({ requestId }) => {
  const [countdown, setCountdown] = useState('');
  const [isWarning, setIsWarning] = useState(false);
  const [isOverdue, setIsOverdue] = useState(false);
  const { data: progressData } = useQuery({
    queryKey: ['orderProgress', requestId],
    queryFn: () => fetchOrderProgress(requestId),
    enabled: !!requestId,
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (!progressData || progressData.status !== 'active') {
      setCountdown('');
      setIsWarning(false);
      setIsOverdue(false);
      return;
    }
    const updateCountdown = () => {
      const now = new Date();
      const isOverdue = progressData.is_overdue;
      const lastUpdated = progressData.last_updated ? new Date(progressData.last_updated) : new Date(progressData.active_at);
      const deadline = new Date(lastUpdated.getTime() + 24 * 60 * 60 * 1000);
      const warningTime = new Date(lastUpdated.getTime() + 20 * 60 * 60 * 1000);
      const timeRemaining = deadline - now;
      if (isOverdue || timeRemaining <= 0) {
        setCountdown('Overdue');
        setIsOverdue(true);
        setIsWarning(true);
      } else {
        const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown(`${hours}h ${minutes}m`);
        setIsOverdue(false);
        setIsWarning(now >= warningTime);
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [progressData]);

  if (!countdown) return null;
  return (
    <p>
      Next confirmation due in{' '}
      <span className={isOverdue ? 'text-red-500' : isWarning ? 'text-yellow-500' : 'text-green-500'}>
        {countdown}
      </span>
      {isWarning && !isOverdue && (
        <span className="text-yellow-500 ml-2">Confirm within 4 hours to avoid cancellation!</span>
      )}
      {isOverdue && (
        <span className="text-red-500 ml-2">Order overdue; awaiting cancellation.</span>
      )}
    </p>
  );
};

function ManageOrders() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orderRefs = useRef({});
  const [error, setError] = useState('');
  const [statusSocket, setStatusSocket] = useState(null);
  const [debtSocket, setDebtSocket] = useState(null);
  const [showJobDonePanel, setShowJobDonePanel] = useState(null);
  const [showPaymentRequestPanel, setShowPaymentRequestPanel] = useState(null);
  const [jobDoneAmount, setJobDoneAmount] = useState('');
  const [payoutPhone, setPayoutPhone] = useState('');
  const [clientPayPhone, setClientPayPhone] = useState('');
  const [paymentStates, setPaymentStates] = useState({});
  const [pendingActiveIncomingPage, setPendingActiveIncomingPage] = useState(1);
  const [completedIncomingPage, setCompletedIncomingPage] = useState(1);
  const [pendingActiveOutgoingPage, setPendingActiveOutgoingPage] = useState(1);
  const [completedOutgoingPage, setCompletedOutgoingPage] = useState(1);
  const [pendingActiveIncomingFilters, setPendingActiveIncomingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [completedIncomingFilters, setCompletedIncomingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [pendingActiveOutgoingFilters, setPendingActiveOutgoingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [completedOutgoingFilters, setCompletedOutgoingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/profile/`, { withCredentials: true });
      return response.data;
    },
    retry: false,
  });

  const { data: providerOrdersRaw, isLoading: providerLoading } = useQuery({
    queryKey: ['providerOrders'],
    queryFn: fetchOrders,
    enabled: !!user?.is_service_provider,
    retry: 2,
    staleTime: 1000 * 60,
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to load incoming orders.');
      toast.error('Failed to load incoming orders.');
    },
  });

  const { data: clientOrdersRaw, isLoading: clientLoading } = useQuery({
    queryKey: ['clientOrders'],
    queryFn: fetchClientOrders,
    enabled: !!user,
    staleTime: 1000 * 60,
    onError: () => toast.error('Failed to load outgoing orders.'),
  });

  const providerOrders = useMemo(() => {
    if (!providerOrdersRaw) return null;
    const incoming_requests = [
      ...(providerOrdersRaw.pending_active_incoming || []),
      ...(providerOrdersRaw.completed_incoming || []),
      ...(providerOrdersRaw.rejected_voided_incoming || []),
    ];
    return { incoming_requests };
  }, [providerOrdersRaw]);

  const clientOrders = useMemo(() => {
    if (!clientOrdersRaw) return null;
    const service_requests = [
      ...(clientOrdersRaw.pending_active_requests || []),
      ...(clientOrdersRaw.completed_requests || []),
      ...(clientOrdersRaw.rejected_voided_requests || []),
    ];
    return { service_requests };
  }, [clientOrdersRaw]);

  const getMinCharge = useCallback((req) => {
    const services = req?.services || [];
    if (services.length === 0) return 300;
    let highestCharge = 0;
    for (const service of services) {
      if (
        service.is_general_category &&
        service.custom_minimum_charge &&
        service.custom_minimum_charge > 0
      ) {
        highestCharge = Math.max(highestCharge, service.custom_minimum_charge);
      }
      else if (service.category_default_min_charge) {
        highestCharge = Math.max(highestCharge, service.category_default_min_charge);
      }
    }
    return highestCharge > 0 ? highestCharge : 300;
  }, []);

  const acceptMutation = useMutation({
    mutationFn: (requestId) =>
      axios.post(`${API_URL}/api/request/${requestId}/accept/`, {}, { withCredentials: true }),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerOrders', 'clientOrders']);
      queryClient.refetchQueries(['providerOrders', 'clientOrders']);
      toast.success('Request accepted!');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to accept'),
  });

  const rejectMutation = useMutation({
    mutationFn: (requestId) =>
      axios.post(`${API_URL}/api/request/${requestId}/reject/`, {}, { withCredentials: true }),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerOrders', 'clientOrders']);
      queryClient.refetchQueries(['providerOrders', 'clientOrders']);
      toast.success('Request rejected');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to reject'),
  });

  const markJobCompleteMutation = useMutation({
    mutationFn: ({ requestId, amount, provider_phone }) =>
      axios.post(
        `${API_URL}/api/request/${requestId}/complete/`,
        { amount, provider_phone },
        { withCredentials: true }
      ),
    onSuccess: () => {
      toast.success('Payment request sent!');
      setShowJobDonePanel(null);
      setJobDoneAmount('');
      setPayoutPhone('');
      queryClient.invalidateQueries(['providerOrders', 'clientOrders']);
      queryClient.refetchQueries(['providerOrders', 'clientOrders']);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to send request'),
  });

  const initiateStkPushMutation = useMutation({
    mutationFn: ({ orderId, phone_number }) =>
      axios.post(
        `${API_URL}/api/order/${orderId}/stk_push/`,
        { phone_number },
        { withCredentials: true }
      ),
    onSuccess: (data, variables) => {
      const orderId = variables.orderId;
      toast.success('STK Push sent! Complete payment on your phone.');
      setShowPaymentRequestPanel(null);
      setClientPayPhone('');
      setPaymentStates(prev => ({ ...prev, [orderId]: 'processing' }));
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old?.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map(req =>
            req.order?.id === orderId
              ? { ...req, order: { ...req.order, status: 'processing' } }
              : req
          ),
        };
      });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'STK Push failed'),
  });

  const handleAccept = useCallback((requestId) => acceptMutation.mutate(requestId), [acceptMutation]);
  const handleReject = useCallback((requestId) => rejectMutation.mutate(requestId), [rejectMutation]);

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.clear();
      navigate('/');
      toast.success('Logged out successfully.');
    } catch {
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const toggleMenu = () => setIsMenuOpen(prev => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const getStatusColor = useMemo(() => (status) => {
    const map = {
      pending: 'text-gray-500',
      scheduled: 'text-yellow-500',
      active: 'text-green-500',
      ready_for_payment: 'text-blue-500',
      pending_payment: 'text-blue-500',
      processing: 'text-blue-500',
      completed: 'text-purple-500',
      rejected: 'text-red-500',
      voided: 'text-red-500',
      cancelled: 'text-red-500',
    };
    return map[status] || 'text-white';
  }, []);

  useEffect(() => {
    if (!user) return;

    let unsubscribeFcm = null;
    if (messaging) {
      unsubscribeFcm = onMessage(messaging, (payload) => {
        const msg = payload.notification?.body || payload.data?.message || 'New update from SErraND';
        toast.success(msg, { duration: 8000, position: 'top-center' });
        queryClient.invalidateQueries(['providerOrders']);
        queryClient.invalidateQueries(['clientOrders']);
      });
    }

    const statusWs = new WebSocket(`${WS_URL}/ws/status/`);

    statusWs.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'status_update') {
        // Force refresh both lists
        queryClient.refetchQueries(['clientOrders']);
        queryClient.refetchQueries(['providerOrders']);

        // PAYMENT SUCCESS → instant UI update
        if (data.order_status === 'completed' && data.order_id) {
          const orderId = data.order_id;
          if (paymentStates[orderId] === 'processing') {
            setPaymentStates(prev => ({ ...prev, [orderId]: 'success' }));
            toast.success('Payment completed successfully!');
          }
        }
      }
    };

    statusWs.onopen = () => console.log('Status WebSocket connected');
    statusWs.onclose = () => console.log('Status WebSocket closed');
    setStatusSocket(statusWs);

    // Auto-reset stuck processing orders (safety net)
    const stuckOrderTimer = setInterval(() => {
      queryClient.setQueryData(['clientOrders'], (oldData) => {
        if (!oldData?.service_requests) return oldData;
        const now = Date.now();
        const updatedRequests = oldData.service_requests.map((req) => {
          if (
            req.order?.status === 'processing' &&
            req.order?.updated_at
          ) {
            const updatedTime = new Date(req.order.updated_at).getTime();
            if (now - updatedTime > 15000) {
              return {
                ...req,
                order: { ...req.order, status: 'pending_payment' },
              };
            }
          }
          return req;
        });
        return { ...oldData, service_requests: updatedRequests };
      });
    }, 10000);

    // 15-second buffer for failed payments
    const timeout = setTimeout(() => {
      Object.keys(paymentStates).forEach(id => {
        if (paymentStates[id] === 'processing') {
          setPaymentStates(prev => ({ ...prev, [id]: 'failed' }));
          toast.error('Payment not detected. Please retry if you paid.');
        }
      });
    }, 15000);

    return () => {
      if (unsubscribeFcm) unsubscribeFcm();
      if (statusWs.readyState === WebSocket.OPEN || statusWs.readyState === WebSocket.CONNECTING) {
        statusWs.close();
      }
      clearInterval(stuckOrderTimer);
      clearTimeout(timeout);
    };
  }, [user, queryClient, messaging, paymentStates]);

  if (userLoading) return <div className="text-center text-white py-12">Loading user data...</div>;
  if (!user || userError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Login Required</h2>
          <div className="flex justify-center gap-4">
            <button onClick={() => navigate('/', { state: { showLogin: true } })} className="px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl font-medium transition">
              Log In
            </button>
            <button onClick={() => navigate('/', { state: { showRegister: true } })} className="px-8 py-4 bg-green-600 hover:bg-green-700 rounded-xl font-medium transition">
              Sign Up
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>
            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>
            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link to="/" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Home</Link>
              <Link to="/profile" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Profile</Link>
              <Link to="/contact" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Contact</Link>
              <Link to="/search" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Search Providers</Link>
              {userLoading || isAuthLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-sm">Loading...</span>
                </div>
              ) : user?.username ? (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-300 text-sm">
                    Jambo, <span className="font-semibold text-white">{user.username}</span>!
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => navigate('/', { state: { showLogin: true } })}
                    className="text-gray-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-800"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/', { state: { showRegister: true } })}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                  >
                    Register
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {isMenuOpen && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50" onClick={closeMenu}></div>
            <div className="absolute top-full left-0 right-0 bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700 shadow-2xl z-50 animate-in slide-in-from-top">
              <div className="px-4 py-6 space-y-4">
                <Link to="/" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" onClick={closeMenu}>Home</Link>
                <Link to="/profile" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" onClick={closeMenu}>Profile</Link>
                <Link to="/contact" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" onClick={closeMenu}>Contact</Link>
                {userLoading || isAuthLoading ? (
                  <div className="flex items-center justify-center space-x-2 py-4">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-400">Loading...</span>
                  </div>
                ) : user?.username ? (
                  <button
                    onClick={() => { handleLogout(); closeMenu(); }}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-lg font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                  >
                    Logout
                  </button>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => { navigate('/', { state: { showLogin: true } }); closeMenu(); }}
                      className="w-full text-gray-300 hover:text-white px-4 py-3 rounded-lg font-medium transition-colors hover:bg-gray-800 border border-gray-600"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => { navigate('/', { state: { showRegister: true } }); closeMenu(); }}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                    >
                      Register
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </nav>

      <div className="orders-content p-6 max-w-7xl mx-auto">
        {error && <div className="text-red-500 text-center mb-6 font-medium">{error}</div>}

        {user?.is_service_provider && providerOrders && (
          <>
            <OrderSection
              title="Active & Pending Incoming Orders"
              orders={providerOrders.incoming_requests.filter(r => !['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={pendingActiveIncomingFilters}
              setFilters={setPendingActiveIncomingFilters}
              currentPage={pendingActiveIncomingPage}
              setCurrentPage={setPendingActiveIncomingPage}
              isCompleted={false}
              isProviderSection={true}
              sectionKey="pendingActiveIncoming"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
            <OrderSection
              title="Completed & Rejected Incoming Orders"
              orders={providerOrders.incoming_requests.filter(r => ['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={completedIncomingFilters}
              setFilters={setCompletedIncomingFilters}
              currentPage={completedIncomingPage}
              setCurrentPage={setCompletedIncomingPage}
              isCompleted={true}
              isProviderSection={true}
              sectionKey="completedIncoming"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
          </>
        )}

        {clientOrders && (
          <>
            <OrderSection
              title="Active & Pending Outgoing Orders"
              orders={clientOrders.service_requests.filter(r => !['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={pendingActiveOutgoingFilters}
              setFilters={setPendingActiveOutgoingFilters}
              currentPage={pendingActiveOutgoingPage}
              setCurrentPage={setPendingActiveOutgoingPage}
              isCompleted={false}
              isProviderSection={false}
              sectionKey="pendingActiveOutgoing"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
            <OrderSection
              title="Completed & Rejected Outgoing Orders"
              orders={clientOrders.service_requests.filter(r => ['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={completedOutgoingFilters}
              setFilters={setCompletedOutgoingFilters}
              currentPage={completedOutgoingPage}
              setCurrentPage={setCompletedOutgoingPage}
              isCompleted={true}
              isProviderSection={false}
              sectionKey="completedOutgoing"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
          </>
        )}

        {(providerLoading || clientLoading) && (
          <div className="text-center text-white py-12">Loading orders...</div>
        )}
      </div>
    </div>
  );
}

export default ManageOrders;
